import type { Connector, Credentials } from "../components/shared/ConnectorCredentialsForm";
import type { TestCredentialsDraftRequest } from "../types/connectivity";

const SUPPORTED_TEST_PMS = new Set(["thais"]);

export function isPmsTestSupported(connectorCode: string): boolean {
  return SUPPORTED_TEST_PMS.has(connectorCode.toLowerCase());
}

export function extractUsernamePassword(
  credentials: Credentials,
  connectorFields?: { key: string }[]
): { username: string; password: string } | null {
  const findValue = (name: string): string => {
    const direct = credentials[name];
    if (typeof direct === "string" && direct.trim()) {
      return direct.trim();
    }
    if (connectorFields) {
      const field = connectorFields.find(
        (f) => f.key.toLowerCase() === name.toLowerCase()
      );
      if (field) {
        const v = credentials[field.key];
        if (typeof v === "string" && v.trim()) {
          return v.trim();
        }
      }
    }
    return "";
  };

  const username = findValue("username");
  const password = findValue("password");

  if (!username || !password) {
    return null;
  }

  return { username, password };
}

export function canRunDraftTest(
  connector: Connector | null,
  credentials: Credentials
): boolean {
  if (!connector || connector.cloudified) {
    return false;
  }

  if (!isPmsTestSupported(connector.code)) {
    return false;
  }

  const baseUrl =
    typeof credentials.base_url === "string" ? credentials.base_url.trim() : "";
  const connectorHotelId =
    typeof credentials.connector_hotel_id === "string"
      ? credentials.connector_hotel_id.trim()
      : "";

  if (!baseUrl || !connectorHotelId) {
    return false;
  }

  const auth = extractUsernamePassword(
    credentials,
    connector.credentials ?? undefined
  );
  return auth !== null;
}

export function buildDraftTestRequest(
  connector: Connector,
  credentials: Credentials
): TestCredentialsDraftRequest {
  const auth = extractUsernamePassword(
    credentials,
    connector.credentials ?? undefined
  );
  if (!auth) {
    throw new Error("Username and password are required to test the connection.");
  }

  const baseUrl =
    typeof credentials.base_url === "string" ? credentials.base_url.trim() : "";
  const connectorHotelId =
    typeof credentials.connector_hotel_id === "string"
      ? credentials.connector_hotel_id.trim()
      : "";

  if (!baseUrl || !connectorHotelId) {
    throw new Error(
      "Connector URL and hotel identifier are required to test the connection."
    );
  }

  return {
    source: "draft",
    pms: connector.code.toLowerCase(),
    baseUrl,
    connectorHotelId,
    credentials: auth,
  };
}
